
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ChatWidget from '../home/components/ChatWidget';

const OperatingSystemsPage = () => {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState(isNL ? 'Populariteit' : 'Popularity');
  const [currentPerPage, setCurrentPerPage] = useState('12');
  const [selectedTab, setSelectedTab] = useState('all');
  const [compareItems, setCompareItems] = useState<string[]>([]);
  const [showCompareBar, setShowCompareBar] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormData, setDownloadFormData] = useState({ name: '', email: '', format: 'pdf' });
  const [downloadSubmitted, setDownloadSubmitted] = useState(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);

  const handleCompareChange = (productName: string, checked: boolean) => {
    if (checked && compareItems.length < 4) {
      const newItems = [...compareItems, productName];
      setCompareItems(newItems);
      setShowCompareBar(true);
    } else if (!checked) {
      const newItems = compareItems.filter(item => item !== productName);
      setCompareItems(newItems);
      if (newItems.length === 0) {
        setShowCompareBar(false);
      }
    }
  };

  const handleDownloadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDownloadSubmitting(true);
    try {
      const formBody = new URLSearchParams();
      formBody.append('name', downloadFormData.name);
      formBody.append('email', downloadFormData.email);
      formBody.append('format', downloadFormData.format);
      await fetch('https://readdy.ai/api/form/d6dgdrmqd308q1a0a1og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });
      setDownloadSubmitted(true);
      setTimeout(() => {
        setShowDownloadModal(false);
        setDownloadSubmitted(false);
        setDownloadFormData({ name: '', email: '', format: 'pdf' });
      }, 2000);
    } catch {
      // silent
    } finally {
      setDownloadSubmitting(false);
    }
  };

  const sortOptions = isNL
    ? ['Populariteit', 'Nieuwste Eerst']
    : ['Popularity', 'Newest'];

  const tabs = isNL
    ? [
        { value: 'all', label: 'Alles' },
        { value: 'personal', label: 'Persoonlijk Gebruik' },
        { value: 'business', label: 'Zakelijk' },
        { value: 'enterprise', label: 'Enterprise' },
      ]
    : [
        { value: 'all', label: 'All' },
        { value: 'personal', label: 'Personal Use' },
        { value: 'business', label: 'Business' },
        { value: 'enterprise', label: 'Enterprise' },
      ];

  const filterTypes = isNL
    ? [
        { label: 'Alle Types', count: 32, checked: true },
        { label: 'Desktop OS', count: 18 },
        { label: 'Server OS', count: 10 },
        { label: 'Mobiel OS', count: 4 },
      ]
    : [
        { label: 'All Types', count: 32, checked: true },
        { label: 'Desktop OS', count: 18 },
        { label: 'Server OS', count: 10 },
        { label: 'Mobile OS', count: 4 },
      ];

  const filterBrands = [
    { label: 'Microsoft', count: 14 },
    { label: 'Apple', count: 4 },
    { label: 'Linux', count: 12 },
    { label: 'Google', count: 2 },
  ];

  const filterLicenseTypes = isNL
    ? [
        { label: 'Persoonlijk', count: 16 },
        { label: 'Zakelijk', count: 14 },
        { label: 'Enterprise', count: 8 },
        { label: 'Open Source', count: 10 },
      ]
    : [
        { label: 'Personal', count: 16 },
        { label: 'Business', count: 14 },
        { label: 'Enterprise', count: 8 },
        { label: 'Open Source', count: 10 },
      ];

  const filterArchitectures = [
    { label: '32-bit', count: 8 },
    { label: '64-bit', count: 28 },
    { label: 'ARM', count: 6 },
  ];

  const filterEditions = isNL
    ? [
        { label: 'Thuis', count: 8 },
        { label: 'Pro', count: 10 },
        { label: 'Enterprise', count: 6 },
        { label: 'Server', count: 8 },
      ]
    : [
        { label: 'Home', count: 8 },
        { label: 'Pro', count: 10 },
        { label: 'Enterprise', count: 6 },
        { label: 'Server', count: 8 },
      ];

  const products = [
    {
      name: 'Windows 11 Pro',
      image: 'https://public.readdy.ai/ai/img_res/d0efb050f088abb461311610b4efa6c4.jpg',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-microsoft-fill',
      iconColor: 'text-blue-600',
      rating: 4.5,
      reviews: 428,
      tags: ['64-bit', isNL ? 'Zakelijk' : 'Business', 'Pro'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-green-50 text-green-600', 'bg-purple-50 text-purple-600'],
      features: isNL
        ? ['Geavanceerde beveiligingsfuncties', 'Extern bureaublad-toegang', 'BitLocker-versleuteling']
        : ['Advanced security features', 'Remote desktop access', 'BitLocker encryption'],
    },
    {
      name: 'macOS Sonoma',
      image: 'https://readdy.ai/api/search-image?query=macOS%20Sonoma%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20sleek%20desktop%20interface%20with%20dock%20and%20menu%20bar%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os2&orientation=landscape',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-apple-fill',
      iconColor: 'text-gray-800',
      rating: 5,
      reviews: 356,
      tags: ['64-bit', isNL ? 'Persoonlijk' : 'Personal', isNL ? 'Gratis Update' : 'Free Update'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-green-50 text-green-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['Naadloze integratie met Apple-apparaten', 'Verbeterde privacyfuncties', 'Continuïteit met iOS-apparaten']
        : ['Seamless integration with Apple devices', 'Enhanced privacy features', 'Continuity with iOS devices'],
    },
    {
      name: 'Ubuntu 22.04 LTS',
      image: 'https://readdy.ai/api/search-image?query=Ubuntu%20Linux%2022.04%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20modern%20desktop%20interface%20with%20dock%20and%20application%20menu%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os3&orientation=landscape',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-ubuntu-fill',
      iconColor: 'text-orange-500',
      rating: 4,
      reviews: 289,
      tags: ['64-bit', 'Open Source', 'LTS'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-green-50 text-green-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['Langdurige ondersteuning (5 jaar)', 'Uitgebreide softwarebibliotheek', 'Sterke beveiligingsfuncties']
        : ['Long-term support (5 years)', 'Extensive software repository', 'Strong security features'],
    },
    {
      name: 'Windows Server 2022',
      image: 'https://readdy.ai/api/search-image?query=Windows%20Server%202022%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20server%20management%20console%20and%20dashboard%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os4&orientation=landscape',
      badge: 'Server OS',
      badgeColor: 'bg-indigo-600',
      icon: 'ri-microsoft-fill',
      iconColor: 'text-blue-600',
      rating: 4,
      reviews: 186,
      tags: ['64-bit', 'Enterprise', 'Datacenter'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-purple-50 text-purple-600', 'bg-indigo-50 text-indigo-600'],
      features: isNL
        ? ['Geavanceerde beveiligingsfuncties', 'Hybride cloudmogelijkheden', 'Storage Spaces Direct']
        : ['Advanced security features', 'Hybrid cloud capabilities', 'Storage Spaces Direct'],
    },
    {
      name: 'Red Hat Enterprise Linux 9',
      image: 'https://readdy.ai/api/search-image?query=Red%20Hat%20Enterprise%20Linux%209%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20server%20management%20console%20and%20terminal%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os5&orientation=landscape',
      badge: 'Server OS',
      badgeColor: 'bg-indigo-600',
      icon: 'ri-red-hat-line',
      iconColor: 'text-red-600',
      rating: 4.5,
      reviews: 214,
      tags: ['64-bit', 'Enterprise', isNL ? 'Abonnement' : 'Subscription'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-purple-50 text-purple-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['Enterprise-ondersteuning', 'Geavanceerde beveiligingsfuncties', 'Gecertificeerde hardwarecompatibiliteit']
        : ['Enterprise-grade support', 'Advanced security features', 'Certified hardware compatibility'],
    },
    {
      name: 'Windows 11 Home',
      image: 'https://readdy.ai/api/search-image?query=Windows%2011%20Home%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20modern%20desktop%20interface%20with%20taskbar%20and%20start%20menu%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os6&orientation=landscape',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-microsoft-fill',
      iconColor: 'text-blue-600',
      rating: 4,
      reviews: 376,
      tags: ['64-bit', isNL ? 'Persoonlijk' : 'Personal', isNL ? 'Thuis' : 'Home'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-green-50 text-green-600', 'bg-purple-50 text-purple-600'],
      features: isNL
        ? ['Moderne gebruikersinterface', 'Microsoft Store-apps', 'Basisbeveiligingsfuncties']
        : ['Modern user interface', 'Microsoft Store apps', 'Basic security features'],
    },
    {
      name: 'Fedora Linux 38',
      image: 'https://readdy.ai/api/search-image?query=Fedora%20Linux%2038%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20modern%20desktop%20interface%20with%20GNOME%20desktop%20environment%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os7&orientation=landscape',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-fedora-fill',
      iconColor: 'text-blue-600',
      rating: 4,
      reviews: 168,
      tags: ['64-bit', 'Open Source', isNL ? 'Nieuwste Technologie' : 'Cutting Edge'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-green-50 text-green-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['Nieuwste softwarepakketten', 'GNOME-desktopomgeving', 'Regelmatige updates']
        : ['Latest software packages', 'GNOME desktop environment', 'Regular updates'],
    },
    {
      name: 'Windows 10 Enterprise LTSC',
      image: 'https://readdy.ai/api/search-image?query=Windows%2010%20Enterprise%20LTSC%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20desktop%20interface%20with%20taskbar%20and%20start%20menu%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os8&orientation=landscape',
      badge: 'Desktop OS',
      badgeColor: 'bg-blue-600',
      icon: 'ri-microsoft-fill',
      iconColor: 'text-blue-600',
      rating: 4.5,
      reviews: 204,
      tags: ['64-bit', 'Enterprise', isNL ? 'Langdurig' : 'Long-term'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-purple-50 text-purple-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['10 jaar ondersteuningscyclus', 'Minimale functie-updates', 'Enterprise-beveiliging']
        : ['10-year support lifecycle', 'Minimal feature updates', 'Enterprise-grade security'],
    },
    {
      name: 'SUSE Linux Enterprise Server 15',
      image: 'https://readdy.ai/api/search-image?query=SUSE%20Linux%20Enterprise%20Server%2015%20operating%20system%20interface%20on%20clean%20white%20background%2C%20professional%20product%20photography%20with%20soft%20shadows%2C%20showing%20the%20server%20management%20console%20and%20terminal%2C%20high%20quality%20digital%20render&width=400&height=250&seq=os9&orientation=landscape',
      badge: 'Server OS',
      badgeColor: 'bg-indigo-600',
      icon: 'ri-server-line',
      iconColor: 'text-green-600',
      rating: 4,
      reviews: 142,
      tags: ['64-bit', 'Enterprise', isNL ? 'Abonnement' : 'Subscription'],
      tagColors: ['bg-blue-50 text-blue-600', 'bg-purple-50 text-purple-600', 'bg-amber-50 text-amber-600'],
      features: isNL
        ? ['Enterprise-ondersteuning', 'Langdurige stabiliteit', 'Gecertificeerd voor SAP-toepassingen']
        : ['Enterprise-grade support', 'Long-term stability', 'Certified for SAP applications'],
    },
  ];

  const sysReqRows = isNL
    ? [
        { component: 'Processor', min: '1 GHz dual-core', rec: '2 GHz quad-core' },
        { component: 'RAM', min: '4 GB', rec: '8 GB' },
        { component: 'Opslag', min: '64 GB', rec: '128 GB' },
        { component: 'Grafisch', min: 'DirectX 12 compatibel', rec: 'Dedicated GPU' },
        { component: 'Beeldscherm', min: '9" 720p', rec: '1080p of hoger' },
      ]
    : [
        { component: 'Processor', min: '1 GHz dual-core', rec: '2 GHz quad-core' },
        { component: 'RAM', min: '4 GB', rec: '8 GB' },
        { component: 'Storage', min: '64 GB', rec: '128 GB' },
        { component: 'Graphics', min: 'DirectX 12 compatible', rec: 'Dedicated GPU' },
        { component: 'Display', min: '9" 720p', rec: '1080p or higher' },
      ];

  const comparisonRows = isNL
    ? [
        { feature: 'Licentietype', values: ['Eeuwigdurend', 'Eigendom', 'Open Source', 'Eeuwigdurend/Abonnement'] },
        { feature: 'Updatecyclus', values: ['Halfjaarlijks', 'Jaarlijks', '2 jaar (LTS)', 'Langdurig'] },
        { feature: 'Beveiligingsfuncties', values: ['Geavanceerd', 'Geavanceerd', 'Sterk', 'Enterprise-niveau'] },
        { feature: 'Hardwarevereisten', values: ['Gemiddeld', 'Alleen Apple-hardware', 'Laag', 'Hoog'] },
        { feature: 'Softwarecompatibiliteit', values: ['Uitstekend', 'Goed', 'Goed', 'Gespecialiseerd'] },
        { feature: 'Gebruikersinterface', values: ['Modern', 'Gepolijst', 'Aanpasbaar', 'Functioneel'] },
        { feature: 'Virtualisatie', values: ['Hyper-V', 'Beperkt', 'KVM', 'Hyper-V (geavanceerd)'] },
      ]
    : [
        { feature: 'License Type', values: ['Perpetual', 'Proprietary', 'Open Source', 'Perpetual/Subscription'] },
        { feature: 'Update Cycle', values: ['Semi-annual', 'Annual', '2 years (LTS)', 'Long-term'] },
        { feature: 'Security Features', values: ['Advanced', 'Advanced', 'Strong', 'Enterprise-grade'] },
        { feature: 'Hardware Requirements', values: ['Moderate', 'Apple hardware only', 'Low', 'High'] },
        { feature: 'Software Compatibility', values: ['Excellent', 'Good', 'Good', 'Specialized'] },
        { feature: 'User Interface', values: ['Modern', 'Polished', 'Customizable', 'Functional'] },
        { feature: 'Virtualization', values: ['Hyper-V', 'Limited', 'KVM', 'Hyper-V (advanced)'] },
      ];

  const licenseCards = isNL
    ? [
        {
          icon: 'ri-user-line',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          title: 'Persoonlijke Licentie',
          features: ['Voor individuele gebruikers', 'Activering op één apparaat', 'Basisondersteuningsopties', 'Standaard functieset', 'Betaalbare prijzen'],
          note: 'Ideaal voor thuisgebruikers en kleine thuiskantoren.',
        },
        {
          icon: 'ri-building-2-line',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Zakelijke Licentie',
          features: ['Voor kleine tot middelgrote bedrijven', 'Volumelicentie-opties', 'Prioriteitsondersteuning', 'Geavanceerde beheerfuncties', 'Flexibele implementatie'],
          note: 'Perfect voor groeiende bedrijven met meerdere gebruikers.',
        },
        {
          icon: 'ri-building-4-line',
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Enterprise Licentie',
          features: ['Voor grote organisaties', 'Gecentraliseerde implementatie', '24/7 premium ondersteuning', 'Geavanceerde beveiligingsfuncties', 'Aangepaste implementatieopties'],
          note: 'Ontworpen voor grootschalige organisatorische implementatie.',
        },
      ]
    : [
        {
          icon: 'ri-user-line',
          iconBg: 'bg-green-100',
          iconColor: 'text-green-600',
          title: 'Personal License',
          features: ['For individual users', 'Single device activation', 'Basic support options', 'Standard feature set', 'Affordable pricing'],
          note: 'Ideal for home users and small home offices.',
        },
        {
          icon: 'ri-building-2-line',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          title: 'Business License',
          features: ['For small to medium businesses', 'Volume licensing options', 'Priority support', 'Advanced management features', 'Flexible deployment'],
          note: 'Perfect for growing businesses with multiple users.',
        },
        {
          icon: 'ri-building-4-line',
          iconBg: 'bg-purple-100',
          iconColor: 'text-purple-600',
          title: 'Enterprise License',
          features: ['For large organizations', 'Centralized deployment', '24/7 premium support', 'Advanced security features', 'Custom implementation options'],
          note: 'Designed for large-scale organizational deployment.',
        },
      ];

  const subscriptionItems = isNL
    ? ['Regelmatige updates', 'Lagere initiële kosten', 'Voorspelbare uitgaven', 'Eenvoudig schaalbaar']
    : ['Regular updates', 'Lower initial cost', 'Predictable expenses', 'Easier to scale'];

  const perpetualItems = isNL
    ? ['Eenmalige betaling', 'Geen terugkerende kosten', 'Langdurig eigendom', 'Beperkte updates']
    : ['One-time payment', 'No recurring fees', 'Long-term ownership', 'Limited updates'];

  const openSourceItems = isNL
    ? [
        'Gratis te gebruiken, wijzigen en distribueren',
        'Community-gedreven ontwikkeling',
        'Transparante codebasis',
        'Verschillende licentietypes (GPL, MIT, enz.)',
        'Optionele betaalde ondersteuning beschikbaar',
      ]
    : [
        'Free to use, modify and distribute',
        'Community-driven development',
        'Transparent code base',
        'Various license types (GPL, MIT, etc.)',
        'Optional paid support available',
      ];

  const usageTypes = isNL
    ? ['Basis', 'Standaard', 'Geavanceerd']
    : ['Basic', 'Standard', 'Advanced'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li className="inline-flex items-center">
                <Link to={prefix || '/'} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-primary">
                  <div className="w-4 h-4 flex items-center justify-center mr-1">
                    <i className="ri-home-line"></i>
                  </div>
                  Home
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                  <a href="#" className="ml-1 text-sm font-medium text-gray-500 hover:text-primary">Software</a>
                </div>
              </li>
              <li aria-current="page">
                <div className="flex items-center">
                  <div className="w-4 h-4 flex items-center justify-center text-gray-400">
                    <i className="ri-arrow-right-s-line"></i>
                  </div>
                  <span className="ml-1 text-sm font-medium text-primary">
                    {isNL ? 'Besturingssystemen' : 'Operating Systems'}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isNL ? 'Besturingssystemen' : 'Operating Systems'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isNL
              ? 'Veilige, betrouwbare besturingssystemen voor elke computeromgeving'
              : 'Secure, reliable operating systems for every computing environment'}
          </p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 sticky top-[137px] z-40 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  placeholder={isNL ? 'Besturingssystemen zoeken...' : 'Search operating systems...'}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-64"
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-search-line"></i>
                </div>
              </div>
              <div className="relative">
                <button
                  onClick={() => setSortOpen(!sortOpen)}
                  className="flex items-center space-x-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none cursor-pointer"
                >
                  <span>{isNL ? 'Sorteren op: ' : 'Sort by: '}</span>
                  <span className="font-medium">{currentSort}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                </button>
                {sortOpen && (
                  <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                    {sortOptions.map((option) => (
                      <a
                        key={option}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentSort(option);
                          setSortOpen(false);
                        }}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        {option}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <button className="w-8 h-8 flex items-center justify-center bg-primary text-white rounded-md cursor-pointer">
                  <i className="ri-grid-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center bg-gray-100 text-gray-500 rounded-md cursor-pointer">
                  <i className="ri-list-check"></i>
                </button>
              </div>
              <div className="relative">
                <button
                  onClick={() => setPerPageOpen(!perPageOpen)}
                  className="flex items-center space-x-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 focus:outline-none cursor-pointer"
                >
                  <span>{isNL ? 'Toon: ' : 'Show: '}</span>
                  <span className="font-medium">{currentPerPage}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                </button>
                {perPageOpen && (
                  <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg py-1 z-10">
                    {['12', '24', '36'].map((option) => (
                      <a
                        key={option}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPerPage(option);
                          setPerPageOpen(false);
                        }}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                      >
                        {option}
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setMobileFilterOpen(true)}
                className="md:hidden flex items-center space-x-1 bg-primary text-white rounded-md px-3 py-2 text-sm cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-filter-3-line"></i>
                </div>
                <span>{isNL ? 'Filters' : 'Filters'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar Filters */}
          <div className="w-full md:w-1/4 lg:w-1/5 md:block hidden">
            <div className="bg-white rounded-lg shadow-sm p-5 sticky top-[202px]">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm font-medium cursor-pointer">
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Type */}
              <div className="border-b border-gray-200 pb-5 mb-5">
                <h4 className="font-medium mb-3">{isNL ? 'Type' : 'Type'}</h4>
                <div className="space-y-2">
                  {filterTypes.map((item) => (
                    <label key={item.label} className="flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked={item.checked} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                      <span className="ml-2 text-sm">{item.label}</span>
                      <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand */}
              <div className="border-b border-gray-200 pb-5 mb-5">
                <h4 className="font-medium mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                <div className="space-y-2">
                  {filterBrands.map((item) => (
                    <label key={item.label} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                      <span className="ml-2 text-sm">{item.label}</span>
                      <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* License Type */}
              <div className="border-b border-gray-200 pb-5 mb-5">
                <h4 className="font-medium mb-3">{isNL ? 'Licentietype' : 'License Type'}</h4>
                <div className="space-y-2">
                  {filterLicenseTypes.map((item) => (
                    <label key={item.label} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                      <span className="ml-2 text-sm">{item.label}</span>
                      <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Architecture */}
              <div className="border-b border-gray-200 pb-5 mb-5">
                <h4 className="font-medium mb-3">{isNL ? 'Architectuur' : 'Architecture'}</h4>
                <div className="space-y-2">
                  {filterArchitectures.map((item) => (
                    <label key={item.label} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                      <span className="ml-2 text-sm">{item.label}</span>
                      <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Edition */}
              <div>
                <h4 className="font-medium mb-3">{isNL ? 'Editie' : 'Edition'}</h4>
                <div className="space-y-2">
                  {filterEditions.map((item) => (
                    <label key={item.label} className="flex items-center cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                      <span className="ml-2 text-sm">{item.label}</span>
                      <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="w-full md:w-3/4 lg:w-4/5">
            {/* Active Filters */}
            <div className="bg-white rounded-lg shadow-sm p-5 mb-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">
                  {isNL ? 'Actieve Filters:' : 'Active Filters:'}
                </span>
                <div className="flex items-center bg-blue-50 text-primary text-xs rounded-full px-3 py-1">
                  {isNL ? 'Type: Alle Types' : 'Type: All Types'}
                  <button className="ml-1 w-4 h-4 flex items-center justify-center cursor-pointer">
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Tab Buttons */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => setSelectedTab(tab.value)}
                    className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                      selectedTab === tab.value
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="relative">
                    <div className="w-full h-48">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <button className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center text-gray-400 hover:text-primary cursor-pointer">
                      <i className="ri-heart-line"></i>
                    </button>
                    <div className={`absolute top-2 left-2 ${product.badgeColor} text-white text-xs font-medium px-2 py-1 rounded`}>
                      {product.badge}
                    </div>
                    <button className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-medium opacity-0 hover:opacity-100 transition-opacity cursor-pointer">
                      <div className="w-5 h-5 flex items-center justify-center mr-1">
                        <i className="ri-eye-line"></i>
                      </div>
                      {isNL ? 'Snel Bekijken' : 'Quick View'}
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center">
                        <div className={`w-6 h-6 flex items-center justify-center mr-2 ${product.iconColor}`}>
                          <i className={product.icon}></i>
                        </div>
                        <h3 className="font-semibold text-gray-800">{product.name}</h3>
                      </div>
                      <div className="text-yellow-400 flex">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center">
                            <i className={i < Math.floor(product.rating) ? 'ri-star-fill' : i < product.rating ? 'ri-star-half-fill' : 'ri-star-line'}></i>
                          </div>
                        ))}
                        <span className="text-xs text-gray-500 ml-1">({product.reviews})</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.tags.map((tag, idx) => (
                        <span key={idx} className={`text-xs font-medium px-2 py-0.5 rounded ${product.tagColors[idx]}`}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <ul className="text-sm text-gray-600 mb-3 space-y-1">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center">
                          <div className="w-4 h-4 flex items-center justify-center text-primary mr-1">
                            <i className="ri-check-line"></i>
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex justify-between items-center mb-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                          onChange={(e) => handleCompareChange(product.name, e.target.checked)}
                          checked={compareItems.includes(product.name)}
                        />
                        <span className="text-xs text-gray-500 ml-1">
                          {isNL ? 'Vergelijken' : 'Compare'}
                        </span>
                      </label>
                    </div>
                    <div className="flex space-x-2">
                      <Link to={`${prefix}/contact`} className="flex-1 bg-primary text-white py-2 rounded-button text-sm font-medium whitespace-nowrap text-center cursor-pointer">
                        {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                      </Link>
                      <button className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-button hover:bg-gray-50 cursor-pointer">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-information-line"></i>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="mt-10 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {isNL ? '1-9 van 32 producten weergegeven' : 'Showing 1-9 of 32 products'}
              </div>
              <div className="flex items-center space-x-1">
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled>
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-primary text-white cursor-pointer">1</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer">2</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer">3</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 cursor-pointer">4</button>
                <button className="w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 cursor-pointer">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
              <div className="hidden md:block w-32"></div>
            </div>
          </div>
        </div>
      </div>

      {/* System Requirements Checker */}
      <div className="container mx-auto px-4 py-8 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary mr-3">
              <i className="ri-cpu-line text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isNL ? 'Systeemvereisten Controleren' : 'System Requirements Checker'}
            </h2>
          </div>
          <p className="text-gray-600 mb-6">
            {isNL
              ? 'Controleer of uw systeem voldoet aan de vereisten voor uw gewenste besturingssysteem.'
              : 'Check if your system meets the requirements for your preferred operating system.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isNL ? 'Selecteer Besturingssysteem' : 'Select Operating System'}
              </label>
              <div className="relative">
                <select className="appearance-none w-full bg-white border border-gray-300 rounded-md py-2 pl-3 pr-8 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer">
                  <option value="">{isNL ? 'Kies een OS' : 'Choose an OS'}</option>
                  <option value="windows-11-pro">Windows 11 Pro</option>
                  <option value="windows-11-home">Windows 11 Home</option>
                  <option value="windows-10-enterprise">Windows 10 Enterprise LTSC</option>
                  <option value="macos-sonoma">macOS Sonoma</option>
                  <option value="ubuntu-22-04">Ubuntu 22.04 LTS</option>
                  <option value="windows-server-2022">Windows Server 2022</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500">
                  <i className="ri-arrow-down-s-line"></i>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isNL ? 'Gebruikstype' : 'Usage Type'}
              </label>
              <div className="flex space-x-4">
                {usageTypes.map((type, idx) => (
                  <label key={type} className="flex items-center cursor-pointer">
                    <input type="radio" name="usage-type" defaultChecked={idx === 0} className="w-4 h-4 text-primary border-gray-300 focus:ring-primary" />
                    <span className="ml-2 text-sm">{type}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isNL ? 'Architectuur' : 'Architecture'}
              </label>
              <div className="flex space-x-4">
                {['64-bit', '32-bit', 'ARM'].map((arch, idx) => (
                  <label key={arch} className="flex items-center cursor-pointer">
                    <input type="radio" name="architecture" defaultChecked={idx === 0} className="w-4 h-4 text-primary border-gray-300 focus:ring-primary" />
                    <span className="ml-2 text-sm">{arch}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {isNL ? 'Systeemvereisten' : 'System Requirements'}
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                      {isNL ? 'Component' : 'Component'}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                      {isNL ? 'Minimum' : 'Minimum'}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                      {isNL ? 'Aanbevolen' : 'Recommended'}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                      {isNL ? 'Uw Systeem' : 'Your System'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sysReqRows.map((row, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-700">{row.component}</td>
                      <td className="py-3 px-4 text-gray-600">{row.min}</td>
                      <td className="py-3 px-4 text-gray-600">{row.rec}</td>
                      <td className="py-3 px-4 text-gray-500">-</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex justify-between items-center">
              <div className="text-gray-600">
                {isNL
                  ? 'Selecteer een besturingssysteem om compatibiliteit te controleren.'
                  : 'Select an operating system to check compatibility.'}
              </div>
              <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap font-medium text-sm cursor-pointer">
                {isNL ? 'Mijn Systeem Controleren' : 'Check My System'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Operating System Comparison */}
      <div className="container mx-auto px-4 py-8 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary mr-3">
              <i className="ri-scales-3-line text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isNL ? 'Besturingssystemen Vergelijken' : 'Operating System Comparison'}
            </h2>
          </div>
          <p className="text-gray-600 mb-6">
            {isNL
              ? 'Vergelijk verschillende besturingssystemen om de beste keuze voor uw behoeften te vinden.'
              : 'Compare different operating systems to find the best fit for your needs.'}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                    {isNL ? 'Functie' : 'Feature'}
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Windows 11 Pro</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">macOS Sonoma</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Ubuntu 22.04 LTS</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Windows Server 2022</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-3 px-4 text-gray-700 font-medium">{row.feature}</td>
                    {row.values.map((value, vIdx) => (
                      <td key={vIdx} className="py-3 px-4 text-gray-600">{value}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex justify-center">
            <button className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap font-medium text-sm cursor-pointer">
              {isNL ? 'Vergelijking Aanpassen' : 'Customize Comparison'}
            </button>
          </div>
        </div>
      </div>

      {/* Operating System License Guide */}
      <div className="container mx-auto px-4 py-8 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary mr-3">
              <i className="ri-file-list-3-line text-xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isNL ? 'Licentiegids voor Besturingssystemen' : 'Operating System License Guide'}
            </h2>
          </div>
          <p className="text-gray-600 mb-6">
            {isNL
              ? 'Begrijp de verschillende licentietypes om de juiste keuze voor uw organisatie te maken.'
              : 'Understanding different license types to make the right choice for your organization.'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {licenseCards.map((license, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-5">
                <div className="flex items-center mb-3">
                  <div className={`w-8 h-8 ${license.iconBg} rounded-full flex items-center justify-center ${license.iconColor} mr-2`}>
                    <i className={license.icon}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800">{license.title}</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-2 mb-4">
                  {license.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-start">
                      <div className="w-4 h-4 flex items-center justify-center text-primary mr-2 mt-0.5">
                        <i className="ri-check-line"></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="text-sm text-gray-500">{license.note}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-600 mr-2">
                  <i className="ri-refresh-line"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {isNL ? 'Abonnement vs. Eeuwigdurend' : 'Subscription vs. Perpetual'}
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    {isNL ? 'Abonnement' : 'Subscription'}
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    {subscriptionItems.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-4 h-4 flex items-center justify-center text-primary mr-2 mt-0.5">
                          <i className="ri-check-line"></i>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-800 mb-2">
                    {isNL ? 'Eeuwigdurend' : 'Perpetual'}
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    {perpetualItems.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-4 h-4 flex items-center justify-center text-primary mr-2 mt-0.5">
                          <i className="ri-check-line"></i>
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mr-2">
                  <i className="ri-open-source-line"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {isNL ? 'Open Source Licenties' : 'Open Source Licensing'}
                </h3>
              </div>
              <ul className="text-sm text-gray-600 space-y-2 mb-4">
                {openSourceItems.map((item, idx) => (
                  <li key={idx} className="flex items-start">
                    <div className="w-4 h-4 flex items-center justify-center text-primary mr-2 mt-0.5">
                      <i className="ri-check-line"></i>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="text-sm text-gray-500">
                {isNL
                  ? 'Ideaal voor kostenbesparende implementaties en aanpassingsbehoeften.'
                  : 'Great for cost-sensitive deployments and customization needs.'}
              </div>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setShowDownloadModal(true)}
              className="text-primary font-medium flex items-center cursor-pointer hover:underline"
            >
              <span>
                {isNL ? 'Download de volledige licentiegids' : 'Download complete licensing guide'}
              </span>
              <div className="w-5 h-5 flex items-center justify-center ml-1">
                <i className="ri-download-line"></i>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Compare Bar */}
      {showCompareBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm font-medium">
                  {isNL ? 'Producten Vergelijken:' : 'Compare Products:'}
                </div>
                <div className="flex items-center space-x-2">
                  {compareItems.map((item, idx) => (
                    <div key={idx} className="bg-blue-50 text-primary text-xs px-3 py-1 rounded-full flex items-center">
                      {item}
                      <button
                        onClick={() => handleCompareChange(item, false)}
                        className="ml-1 w-4 h-4 flex items-center justify-center cursor-pointer"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">
                  {isNL
                    ? `${compareItems.length} van 4 items geselecteerd`
                    : `${compareItems.length} of 4 items selected`}
                </div>
                <button
                  className="bg-primary text-white px-4 py-2 rounded-button whitespace-nowrap text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  disabled={compareItems.length < 2}
                >
                  {isNL ? 'Vergelijken' : 'Compare'}
                </button>
                <button
                  onClick={() => {
                    setCompareItems([]);
                    setShowCompareBar(false);
                  }}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 bg-black/50 z-50">
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-lg">{isNL ? 'Filters' : 'Filters'}</h3>
              <button
                onClick={() => setMobileFilterOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4">
              <div className="space-y-6">
                {/* Type */}
                <div>
                  <h4 className="font-medium mb-3">{isNL ? 'Type' : 'Type'}</h4>
                  <div className="space-y-2">
                    {filterTypes.map((item) => (
                      <label key={item.label} className="flex items-center cursor-pointer">
                        <input type="checkbox" defaultChecked={item.checked} className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                        <span className="ml-2 text-sm">{item.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {/* Brand */}
                <div>
                  <h4 className="font-medium mb-3">{isNL ? 'Merk' : 'Brand'}</h4>
                  <div className="space-y-2">
                    {filterBrands.map((item) => (
                      <label key={item.label} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                        <span className="ml-2 text-sm">{item.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {/* License Type */}
                <div>
                  <h4 className="font-medium mb-3">{isNL ? 'Licentietype' : 'License Type'}</h4>
                  <div className="space-y-2">
                    {filterLicenseTypes.map((item) => (
                      <label key={item.label} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                        <span className="ml-2 text-sm">{item.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {/* Architecture */}
                <div>
                  <h4 className="font-medium mb-3">{isNL ? 'Architectuur' : 'Architecture'}</h4>
                  <div className="space-y-2">
                    {filterArchitectures.map((item) => (
                      <label key={item.label} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                        <span className="ml-2 text-sm">{item.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {/* Edition */}
                <div>
                  <h4 className="font-medium mb-3">{isNL ? 'Editie' : 'Edition'}</h4>
                  <div className="space-y-2">
                    {filterEditions.map((item) => (
                      <label key={item.label} className="flex items-center cursor-pointer">
                        <input type="checkbox" className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary" />
                        <span className="ml-2 text-sm">{item.label}</span>
                        <span className="ml-auto text-xs text-gray-500">{item.count}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setMobileFilterOpen(false)}
                className="w-full bg-primary text-white py-2 rounded-button font-medium cursor-pointer whitespace-nowrap"
              >
                {isNL ? 'Filters Toepassen' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg w-full max-w-md mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isNL ? 'Licentiegids Downloaden' : 'Download Licensing Guide'}
                </h3>
                <button
                  onClick={() => { setShowDownloadModal(false); setDownloadSubmitted(false); }}
                  className="text-gray-400 hover:text-gray-500 cursor-pointer"
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-close-line"></i>
                  </div>
                </button>
              </div>
              {downloadSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-3xl"></i>
                  </div>
                  <p className="text-gray-700 font-medium">
                    {isNL ? 'Succesvol verzonden! De gids wordt naar uw e-mail gestuurd.' : 'Successfully submitted! The guide will be sent to your email.'}
                  </p>
                </div>
              ) : (
                <form
                  id="os-licensing-guide-form"
                  data-readdy-form
                  onSubmit={handleDownloadSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isNL ? 'Naam' : 'Name'}
                    </label>
                    <input
                      name="name"
                      required
                      value={downloadFormData.name}
                      onChange={(e) => setDownloadFormData({ ...downloadFormData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {isNL ? 'E-mailadres' : 'Email'}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={downloadFormData.email}
                      onChange={(e) => setDownloadFormData({ ...downloadFormData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isNL ? 'Formaat' : 'Format'}
                    </label>
                    <div className="space-y-2">
                      {[
                        { value: 'pdf', label: isNL ? 'PDF-document' : 'PDF Document' },
                        { value: 'doc', label: isNL ? 'Word-document' : 'Word Document' },
                      ].map((format) => (
                        <label key={format.value} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="format"
                            value={format.value}
                            checked={downloadFormData.format === format.value}
                            onChange={(e) => setDownloadFormData({ ...downloadFormData, format: e.target.value })}
                            className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                          />
                          <span className="ml-2 text-sm">{format.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={downloadSubmitting}
                      className="w-full bg-primary text-white py-2 rounded-button font-medium flex items-center justify-center cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      <div className="w-5 h-5 flex items-center justify-center mr-1">
                        <i className="ri-download-line"></i>
                      </div>
                      {downloadSubmitting
                        ? (isNL ? 'Verzenden...' : 'Submitting...')
                        : (isNL ? 'Nu Downloaden' : 'Download Now')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default OperatingSystemsPage;
